import { nostojs } from "@nosto/nosto-js"
import { customElement } from "../decorators"
import { NostoElement } from "../Element"

/**
 * A custom element that provides conditional content rendering based on user segments.
 * This component replaces its children with the content of the first template
 * that matches any of the current user's Nosto segments.
 *
 * @category Store level templating
 *
 * @example
 * Conditional content based on user segments:
 * ```html
 * <nosto-control>
 *   <template segment="5b71f1500000000000000006">
 *     <div class="premium-offer">
 *       <h2>Exclusive Premium Offer!</h2>
 *       <p>Get 20% off your next purchase</p>
 *     </div>
 *   </template>
 *   <template segment="5a497a000000000000000001">
 *     <div class="welcome-offer">
 *       <h2>Welcome! Get Started</h2>
 *       <p>Use code WELCOME10 for 10% off your first order</p>
 *     </div>
 *   </template>
 *   <template segment="5a497a000000000000000002">
 *     <div class="loyalty-message">
 *       <h2>Thank you for your loyalty!</h2>
 *       <p>Free shipping on all orders this month</p>
 *     </div>
 *   </template>
 * </nosto-control>
 * ```
 *
 * @example
 * Control element with fallback content when no segments match:
 * ```html
 * <nosto-control>
 *   <template segment="5b71f1500000000000000006">
 *     <div class="premium-offer">
 *       <h2>VIP Members Only</h2>
 *       <p>Exclusive early access to new arrivals</p>
 *     </div>
 *   </template>
 *
 *   <template segment="5a497a000000000000000001">
 *     <div class="first-time-visitor">
 *       <h2>First time here?</h2>
 *       <p>Sign up for 15% off your first order</p>
 *     </div>
 *   </template>
 *
 *   <div class="default-content">
 *     <h2>Welcome to our store!</h2>
 *     <p>Discover our latest products and special offers</p>
 *     <a href="/collections/all" class="browse-all">Browse All Products</a>
 *   </div>
 * </nosto-control>
 * ```
 */
@customElement("nosto-control")
export class Control extends NostoElement {
  async connectedCallback() {
    const api = await new Promise(nostojs)
    const segments = await api.internal.getSegments()
    const template = Array.from(this.querySelectorAll<HTMLTemplateElement>(":scope > template[segment]")).find(el =>
      // TODO provide more filtering options: schedule, affinities etc
      segments?.includes(el.getAttribute("segment")!)
    )
    if (template) {
      const clone = template.content.cloneNode(true) as DocumentFragment
      this.replaceChildren(...clone.childNodes)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "nosto-control": Control
  }
}
